"use client"

import type React from "react"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { LiquidCard, CardContent, CardHeader } from "@/components/ui/liquid-glass-card"
import { Badge } from "@/components/ui/badge"

// Types and Enums
export enum Strength {
  None = "none",
  Weak = "weak",
  Moderate = "moderate",
  Strong = "strong",
}

export interface FinancialScoreProps {
  title: string
  description: string
  initialScore?: number
  maxScore?: number
  suffix?: string
  detailUrl?: string
  valuePlus?: boolean
}

interface FinancialScoreCardProps {
  children?: React.ReactNode
}

interface FinancialScoreDisplayProps {
  value: Score
  max: number
  suffix?: string
  valuePlus?: boolean
}

interface FinancialScoreHalfCircleProps {
  value: Score
  max: number
}

interface FinancialScoreHeaderProps {
  title?: string
  strength?: Strength
}

type CounterContextType = {
  getNextIndex: () => number
}

type Score = number | null
type StrengthColors = Record<Strength, string[]>

// Utils Class
class Utils {
  static LOCALE = "pl-PL"

  static easings = {
    easeInOut: "cubic-bezier(0.65, 0, 0.35, 1)",
    easeOut: "cubic-bezier(0.33, 1, 0.68, 1)",
  }

  static circumference(r: number): number {
    return 2 * Math.PI * r
  }

  static formatNumber(n: number) {
    return new Intl.NumberFormat(this.LOCALE).format(n)
  }

  static getStrength(score: Score, maxScore: number): Strength {
    if (!score) return Strength.None

    const percent = score / maxScore

    if (percent >= 0.8) return Strength.Strong
    if (percent >= 0.4) return Strength.Moderate

    return Strength.Weak
  }

  static randomHash(length = 4): string {
    const chars = "abcdef0123456789"
    const bytes = crypto.getRandomValues(new Uint8Array(length))

    return [...bytes].map((b) => chars[b % chars.length]).join("")
  }

  static randomInt(min = 0, max = 1): number {
    const value = crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32

    return Math.round(min + (max - min) * value)
  }
}

// Context
const CounterContext = createContext<CounterContextType | undefined>(undefined)

const CounterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const counterRef = useRef(0)
  const getNextIndex = useCallback(() => {
    return counterRef.current++
  }, [])

  return <CounterContext.Provider value={{ getNextIndex }}>{children}</CounterContext.Provider>
}

const useCounter = () => {
  const context = useContext(CounterContext)

  if (!context) {
    throw new Error("useCounter must be used within a CounterProvider")
  }

  return context.getNextIndex
}

// Components
function FinancialScoreCard({ children }: FinancialScoreCardProps) {
  const getNextIndex = useCounter()
  const indexRef = useRef<number | null>(null)
  const animationRef = useRef<number | ReturnType<typeof setTimeout>>(0)
  const [appearing, setAppearing] = useState(false)

  if (indexRef.current === null) {
    indexRef.current = getNextIndex()
  }

  useEffect(() => {
    const delayInc = 200
    const delay = 300 + indexRef.current! * delayInc

    animationRef.current = setTimeout(() => setAppearing(true), delay)

    return () => {
      clearTimeout(animationRef.current as number)
    }
  }, [])

  if (!appearing) return null

  return (
    <LiquidCard className="w-full h-full flex animate-in fade-in slide-in-from-bottom-8 duration-800 fill-mode-both border-stroke">
      <CardContent className="p-7 xl:p-9 flex flex-col w-full">{children}</CardContent>
    </LiquidCard>
  )
}

function FinancialScoreDisplay({ value, max, suffix, valuePlus }: FinancialScoreDisplayProps) {
  const hasValue = value !== null
  const digits = String(Math.floor(value || 0)).split("")
  const label = hasValue && suffix ? suffix : (hasValue ? `na ${Utils.formatNumber(max)}` : "Brak wyniku")

  return (
    <div className="absolute bottom-0 w-full text-center">
      <div className="text-4xl font-medium h-15 overflow-hidden relative text-text-primary flex justify-center items-end">
        <div className="absolute inset-0 opacity-0">
          <div className="inline-block">0</div>
        </div>
        <div className="relative flex">
          {hasValue &&
            digits.map((digit, i) => (
              <span
                key={i}
                className="inline-block animate-in slide-in-from-bottom-full duration-800 delay-400 fill-mode-both"
                style={{
                  animationDelay: `${400 + i * 100}ms`,
                  animationDuration: `${800 + i * 300}ms`,
                }}
              >
                {digit}
              </span>
            ))}
          {hasValue && valuePlus && (
            <span
              className="inline-block animate-in slide-in-from-bottom-full duration-800 delay-400 fill-mode-both"
              style={{
                animationDelay: `${400 + digits.length * 100}ms`,
                animationDuration: `${800 + digits.length * 300}ms`,
              }}
            >
              +
            </span>
          )}
        </div>
      </div>
      <div className="text-sm text-muted uppercase tracking-wide mt-2">{label}</div>
    </div>
  )
}

function FinancialScoreHalfCircle({ value, max }: FinancialScoreHalfCircleProps) {
  const strokeRef = useRef<SVGCircleElement>(null)
  const gradIdRef = useRef(`grad-${Utils.randomHash()}`)
  const gradId = gradIdRef.current
  const gradStroke = `url(#${gradId})`
  const radius = 45
  const dist = Utils.circumference(radius)
  const distHalf = dist / 2
  const distFourth = distHalf / 2
  const strokeDasharray = `${distHalf} ${distHalf}`
  const distForValue = Math.min((value as number) / max, 1) * -distHalf
  const strokeDashoffset = value !== null ? distForValue : -distFourth
  const strength = Utils.getStrength(value, max)
  const strengthColors: StrengthColors = {
    none: ["hsl(var(--stroke))", "hsl(var(--stroke))"],
    weak: ["hsl(var(--accent-from))", "hsl(var(--accent-to))"],
    moderate: ["hsl(var(--accent-from))", "hsl(var(--accent-to))"],
    strong: ["hsl(var(--accent-from))", "hsl(var(--accent-to))"],
  }
  const colorStops = strengthColors[strength]

  useEffect(() => {
    const strokeStart = 400
    const duration = 1400

    strokeRef.current?.animate(
      [
        { strokeDashoffset: "0", offset: 0 },
        { strokeDashoffset: "0", offset: strokeStart / duration },
        { strokeDashoffset: strokeDashoffset.toString() },
      ],
      {
        duration,
        easing: Utils.easings.easeInOut,
        fill: "forwards",
      },
    )
  }, [value, max, strokeDashoffset])

  return (
    <svg className="block mx-auto w-auto max-w-full h-36" viewBox="0 0 100 50" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
          {colorStops.map((stop, i) => {
            const offset = `${(100 / (colorStops.length - 1)) * i}%`
            return <stop key={i} offset={offset} stopColor={stop} />
          })}
        </linearGradient>
      </defs>
      <g fill="none" strokeWidth="10" transform="translate(50, 50.5)">
        <circle className="stroke-stroke/30" r={radius} />
        <circle ref={strokeRef} stroke={gradStroke} strokeDasharray={strokeDasharray} r={radius} />
      </g>
    </svg>
  )
}

function FinancialScoreHeader({ title, strength }: FinancialScoreHeaderProps) {
  const hasStrength = strength !== Strength.None

  return (
    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-10 px-0 animate-in fade-in slide-in-from-bottom-12 duration-800 delay-0">
      <h2 className="text-lg font-semibold text-text-primary leading-snug">{title}</h2>
      {hasStrength && (
        <Badge
          variant="outline"
          className="uppercase text-xs font-semibold shrink-0 animate-in fade-in slide-in-from-bottom-12 h-8 duration-800 delay-800 text-text-primary border-stroke"
        >
          {strength === 'strong' ? 'Wysoki' : strength === 'moderate' ? 'Średni' : 'Niski'}
        </Badge>
      )}
    </CardHeader>
  )
}

export function FinancialScore({ title, description, initialScore, maxScore: customMax, suffix, detailUrl, valuePlus }: FinancialScoreProps) {
  const score: Score = initialScore ?? null
  const max = customMax ?? 100
  const strength = Utils.getStrength(score, max)

  return (
    <FinancialScoreCard>
      <FinancialScoreHeader title={title} strength={strength} />
      <div className="relative mb-8 animate-in fade-in slide-in-from-bottom-12 duration-800 delay-100">
        <FinancialScoreHalfCircle value={score} max={max} />
        <FinancialScoreDisplay value={score} max={max} suffix={suffix} valuePlus={valuePlus} />
      </div>
      <p className="text-text-primary/70 text-sm text-center mb-9 leading-relaxed animate-in fade-in slide-in-from-bottom-12 duration-800 delay-200">
        {description}
      </p>
      {detailUrl ? (
        <Link
          to={detailUrl}
          className="mt-auto w-full inline-flex items-center justify-center h-16 text-base font-medium rounded-full bg-text-primary text-bg hover:scale-[1.02] transition-transform animate-in fade-in slide-in-from-bottom-12 duration-800 delay-300"
        >
          Pełna historia →
        </Link>
      ) : null}
    </FinancialScoreCard>
  )
}

export function FinancialScoreCardsWrapper({ data }: { data: FinancialScoreProps[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
      <CounterProvider>
        {data.map((card, i) => (
          <FinancialScore key={i} {...card} />
        ))}
      </CounterProvider>
    </div>
  )
}
